import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Loader2, 
  ArrowRight,
  Sparkles,
  X,
  ChevronLeft,
  ChevronRight,
  Layout,
  BookOpen,
  Target,
  Palette,
} from 'lucide-react';
// ChevronLeft, ChevronRight, Layout, BookOpen used in web view below
import { langchainResumeParser, langchainDatabaseService } from '@soVendor/services/langchainService';
import { supabase } from '@soVendor/lib/supabase';
import { ResumeData } from '../shared/types';
import { parseResumeSections, structuredResumeToSections, arrangeResumeSections, bucketResumeSections, sortSectionsByCanonicalOrder } from '../utils/parseResumeSections';
import {
  STEP_WRAPPER_CLASS,
  STEP_MOTION_CLASS,
  STEP_SCROLL_AREA_CLASS,
  STEP_INNER_HEADER_CLASS,
  STEP_HEADER_ICON_CLASS,
  STEP_PRIMARY_CTA_CLASS,
} from '../shared/stepLayout';
import { RESULTS_TEMPLATES, TEMPLATE_LIBRARY, TemplateLibraryModal, ResumeDocumentContent } from '../ResultsStep';
import { OptimizerMoreMenu } from '../OptimizerMoreMenu';

type InputMode = 'upload' | 'ai';

export type RoleFitUploadContext = {
  jobTitle: string;
  company: string;
  description: string;
  profileResumeText: string;
  candidateLabel: string;
  candidateName?: string;
  candidateEmail?: string;
  /** CRM ids for compact display in candidate card */
  candidateId?: string;
  jobId?: string;
  /** True when user came from /role-fit-ai */
  fromRoleFitAi?: boolean;
};

interface UploadStepProps {
  onNext: (data: ResumeData, templateId?: string) => void;
  onBack: () => void;
  resumeData?: ResumeData;
  selectedTemplateId?: string;
  onFileUpload: (file: File) => Promise<ResumeData>;
  isLoading: boolean;
  error: string | null;
  /** RoleFit AI: loading candidate + job from CRM */
  roleFitPrefetchLoading?: boolean;
  roleFitFetchError?: string | null;
  /** RoleFit AI: prefilled JD + profile text for the “profile” tab */
  roleFitContext?: RoleFitUploadContext;
}

const BRAND_CARD_BORDER: React.CSSProperties = { border: '1px solid var(--brand-input-border)' };
/** RoleFit / optimizer cards: aligned with STEP_CARD_COMPACT (no bright white in dark UI) */
const ROLEFIT_CARD_CLASS =
  'flex min-h-0 flex-col overflow-hidden rounded-2xl bg-[var(--vscode-bg-elevated)] shadow-sm border border-[var(--vscode-border-primary)]';
const WEB_CTA_BUTTON_CLASS = `flex w-full items-center justify-center gap-2 ${STEP_PRIMARY_CTA_CLASS} px-4 py-3`;

/** Full-bleed strip inside STEP_WRAPPER (cancels horizontal padding so RoleFit footer aligns with shell edges). */
const ROLEFIT_FOOTER_STRIP_CLASS =
  '-mx-3 xs:-mx-4 sm:-mx-5 lg:-mx-8 w-[calc(100%+1.5rem)] xs:w-[calc(100%+2rem)] sm:w-[calc(100%+2.5rem)] lg:w-[calc(100%+4rem)] max-w-none border-t border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_92%,var(--vscode-bg-elevated))] px-3 py-4 xs:px-4 sm:px-5 lg:px-8 sm:py-5';

/** Key for persisting upload state across tab/window switches */
const UPLOAD_DRAFT_KEY = 'opt_upload_draft';

const UploadStep: React.FC<UploadStepProps> = ({
  onNext,
  onBack,
  resumeData: initialResumeData,
  selectedTemplateId: initialTemplateId = 'ats-optimized',
  isLoading,
  error,
  roleFitPrefetchLoading = false,
  roleFitFetchError = null,
  roleFitContext,
}) => {
  const { t } = useTranslation();
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  /** RoleFit web/native: resume source — preloaded CRM text, file upload, or AI generation */
  const [roleFitMainTab, setRoleFitMainTab] = useState<'preloaded' | 'upload' | 'ai'>('preloaded');
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState<string>('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [hasResume, setHasResume] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [parsingMetadata, setParsingMetadata] = useState<any>(null);
  const [parsedStructured, setParsedStructured] = useState<Record<string, unknown[]> | null>(null);
  const [parsedSectionsFromDocument, setParsedSectionsFromDocument] = useState<Array<{ title: string; content: string; preview: string }> | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGeneratedText, setAiGeneratedText] = useState<string | null>(null);
  const [aiGenerateLoading, setAiGenerateLoading] = useState(false);
  const validTemplateIds = React.useMemo(() => RESULTS_TEMPLATES.map((t) => t.id), []);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() =>
    validTemplateIds.includes(initialTemplateId) ? initialTemplateId : 'ats-optimized'
  );
  const carouselRef = useRef<HTMLDivElement>(null); // kept for potential future use
  const [carouselIdx, setCarouselIdx] = useState<number>(() => {
    const id = validTemplateIds.includes(initialTemplateId) ? initialTemplateId : 'ats-optimized';
    const idx = TEMPLATE_LIBRARY.findIndex(t => t.id === id);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    const id = validTemplateIds.includes(initialTemplateId) ? initialTemplateId : 'ats-optimized';
    setSelectedTemplateId(id);
    const idx = TEMPLATE_LIBRARY.findIndex(t => t.id === id);
    if (idx >= 0) setCarouselIdx(idx);
  }, [initialTemplateId, validTemplateIds]);

  useEffect(() => {
    if (roleFitContext) {
      setInputMode('upload');
    }
  }, [roleFitContext]);

  const totalTemplates = TEMPLATE_LIBRARY.length;
  const wrapIdx = (i: number) => ((i % totalTemplates) + totalTemplates) % totalTemplates;

  const handleCarouselPrev = () => setCarouselIdx(prev => wrapIdx(prev - 1));
  const handleCarouselNext = () => setCarouselIdx(prev => wrapIdx(prev + 1));

  const handleSelectCarouselTemplate = (idx: number) => {
    setCarouselIdx(idx);
    setSelectedTemplateId(TEMPLATE_LIBRARY[idx].id);
  };

  // ── Restore upload draft from sessionStorage on mount ────────────────────
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(UPLOAD_DRAFT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.resumeText && saved.fileName) {
        setResumeText(saved.resumeText);
        setHasResume(true);
        setParsingMetadata(saved.parsingMetadata ?? null);
        setParsedStructured(saved.parsedStructured ?? null);
        setParsedSectionsFromDocument(saved.parsedSectionsFromDocument ?? null);
        // Recreate a stub File so the UI shows the filename / size
        setResumeFile(new File([saved.resumeText], saved.fileName, { type: saved.fileType || 'text/plain' }));
      }
    } catch { /* ignore */ }
  }, []);

  // ── Persist upload draft to sessionStorage whenever extracted state changes
  useEffect(() => {
    if (!resumeText || !resumeFile) {
      sessionStorage.removeItem(UPLOAD_DRAFT_KEY);
      return;
    }
    try {
      sessionStorage.setItem(UPLOAD_DRAFT_KEY, JSON.stringify({
        resumeText,
        fileName: resumeFile.name,
        fileType: resumeFile.type,
        fileSize: resumeFile.size,
        parsingMetadata,
        parsedStructured,
        parsedSectionsFromDocument,
      }));
    } catch { /* ignore quota errors */ }
  }, [resumeText, resumeFile, parsingMetadata, parsedStructured, parsedSectionsFromDocument]);

  const clearResume = () => {
    setResumeFile(null);
    setResumeText('');
    setHasResume(false);
    setExtractionError(null);
    setParsingMetadata(null);
    setParsedStructured(null);
    setParsedSectionsFromDocument(null);
    sessionStorage.removeItem(UPLOAD_DRAFT_KEY);
  };


  const PARSE_TIMEOUT_MS = 90_000;

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLocalError(null);
    setExtractionError(null);
    setIsExtracting(true);
    setResumeFile(file);

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Parsing took too long. Please try again or use a smaller file.')), PARSE_TIMEOUT_MS)
    );

    try {
      console.log('[UploadStep] Starting universal resume parsing for:', file.name, file.type);

      const parsedData = await Promise.race([
        langchainResumeParser.parseResume(file),
        timeoutPromise
      ]);
      
      console.log('[UploadStep] Resume parsed successfully:', {
        textLength: parsedData.rawText.length,
        format: parsedData.metadata.format,
        confidence: parsedData.metadata.confidence,
        processingTime: parsedData.metadata.processingTime
      });

      setResumeText(parsedData.rawText);
      setHasResume(true);
      setParsingMetadata(parsedData.metadata);
      setParsedStructured((parsedData as { structured?: Record<string, unknown[]> }).structured ?? null);
      setParsedSectionsFromDocument((parsedData as { sections?: Array<{ title: string; content: string; preview: string }> }).sections ?? null);

      let resumeId: string | null = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          resumeId = await langchainDatabaseService.saveResumeData(user.id, file.name, parsedData);
          console.log('[UploadStep] Resume data saved to database with ID:', resumeId);
        }
      } catch (dbError) {
        console.warn('[UploadStep] Database save failed, will use temporary ID:', dbError);
      }
      
      setParsingMetadata({
        ...parsedData.metadata,
        savedResumeId: resumeId
      });

    } catch (err) {
      console.error('[UploadStep] Resume parsing failed:', err);
      setExtractionError(err instanceof Error ? err.message : 'Failed to parse resume');
      setHasResume(false);
    } finally {
      setIsExtracting(false);
    }
  };

  useEffect(() => () => setIsExtracting(false), []);

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const syntheticEvent = {
        target: { files: [file] }
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileUpload(syntheticEvent);
    }
  };

  const handleNext = () => {
    if (hasResume && resumeFile) {
      const resumeId = parsingMetadata?.savedResumeId || null;
      const text = resumeText || '';
      const fromDocument = parsedSectionsFromDocument && parsedSectionsFromDocument.length > 0 ? parsedSectionsFromDocument : null;
      const fromStructured = structuredResumeToSections(parsedStructured);
      const rawSections = fromStructured.length > 0 ? fromStructured : parseResumeSections(text);
      const sectionList =
        fromDocument != null
          ? sortSectionsByCanonicalOrder(fromDocument)
          : bucketResumeSections(arrangeResumeSections(rawSections));
      const resumeData: ResumeData = {
        id: resumeId || 'temp-resume-id',
        fileName: resumeFile.name,
        fileType: resumeFile.type.includes('pdf') ? 'pdf' : 
                  resumeFile.type.includes('word') ? 'docx' : 
                  resumeFile.type.includes('image') ? 'image' :
                  resumeFile.type.includes('html') ? 'html' : 'txt',
        fileSize: resumeFile.size,
        parsedText: text,
        sections: sectionList,
        structured: parsedStructured ?? undefined,
        metadata: {
          wordCount: resumeText?.split(/\s+/).length || 0,
          pageCount: parsingMetadata?.pageCount || 1,
          lastModified: new Date(),
          langchainParsed: true,
          confidence: parsingMetadata?.confidence,
          processingTime: parsingMetadata?.processingTime
        },
        uploadDate: new Date()
      };
      onNext(resumeData);
    }
  };

  const isContinueLoading = isExtracting || isLoading;

  const skillsDatabase = [
    'JavaScript', 'TypeScript', 'Python', 'Java', 'React', 'Node.js', 'Angular', 'Vue.js',
    'HTML', 'CSS', 'SQL', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker', 'Kubernetes',
    'Git', 'Agile', 'Scrum', 'Project Management', 'Leadership', 'Communication',
    'Machine Learning', 'Data Analysis', 'UI/UX', 'REST API', 'GraphQL', 'Redux'
  ];

  const detectedSkills = React.useMemo(() => {
    if (!resumeText) return [];
    const resumeTextLower = resumeText.toLowerCase();
    return skillsDatabase.filter(skill => 
      resumeTextLower.includes(skill.toLowerCase())
    ).slice(0, 12);
  }, [resumeText]);

  const resumeDataForReview = React.useMemo((): ResumeData | null => {
    if (!hasResume || !resumeFile) return null;
    const text = resumeText || '';
    const fromDocument = parsedSectionsFromDocument && parsedSectionsFromDocument.length > 0 ? parsedSectionsFromDocument : null;
    const fromStructured = structuredResumeToSections(parsedStructured);
    const rawSections = fromStructured.length > 0 ? fromStructured : parseResumeSections(text);
    const sectionList =
      fromDocument != null
        ? sortSectionsByCanonicalOrder(fromDocument)
        : bucketResumeSections(arrangeResumeSections(rawSections));
    return {
      id: parsingMetadata?.savedResumeId || 'temp-resume-id',
      fileName: resumeFile.name,
      fileType: resumeFile.type.includes('pdf') ? 'pdf' : resumeFile.type.includes('word') ? 'docx' : resumeFile.type.includes('image') ? 'image' : resumeFile.type.includes('html') ? 'html' : 'txt',
      fileSize: resumeFile.size,
      parsedText: text,
      sections: sectionList,
      structured: parsedStructured ?? undefined,
      metadata: {
        wordCount: resumeText?.split(/\s+/).length || 0,
        pageCount: parsingMetadata?.pageCount || 1,
        lastModified: new Date(),
        langchainParsed: true,
        confidence: parsingMetadata?.confidence,
        processingTime: parsingMetadata?.processingTime
      },
      uploadDate: new Date()
    };
  }, [hasResume, resumeFile, resumeText, parsedSectionsFromDocument, parsedStructured, parsingMetadata]);

  const handleContinueToPreview = () => {
    if (hasResume && resumeDataForReview) {
      // Clear draft — from here the SingleOptimizeFlow autosave takes over
      sessionStorage.removeItem(UPLOAD_DRAFT_KEY);
      onNext(resumeDataForReview, selectedTemplateId);
    }
  };

  const applyRoleFitProfile = () => {
    if (!roleFitContext) return;
    const text = roleFitContext.profileResumeText.trim();
    if (!text) {
      setLocalError('No profile resume text is available for this candidate.');
      return;
    }
    setLocalError(null);
    setExtractionError(null);
    setResumeText(text);
    setHasResume(true);
    const baseName =
      roleFitContext.candidateLabel
        .replace(/[^a-zA-Z0-9\s.-]+/g, '')
        .trim()
        .slice(0, 48)
        .replace(/\s+/g, '-') || 'candidate';
    setResumeFile(new File([text], `${baseName}-profile.txt`, { type: 'text/plain' }));
    setParsedSectionsFromDocument(null);
    setParsedStructured(null);
    setParsingMetadata({ confidence: 0.82, pageCount: 1, processingTime: 0 });
    setRoleFitMainTab('upload');
  };

  const continueCtaLabel = roleFitContext
    ? t('optimizer.continueToMatchAnalysis', 'Continue to match analysis')
    : t('optimizer.continueToPreview', 'Continue to Preview');

  const continueDisabled =
    !hasResume ||
    !resumeDataForReview ||
    isContinueLoading ||
    roleFitPrefetchLoading ||
    !!roleFitFetchError;

  const selectedTemplateCfg = TEMPLATE_LIBRARY.find(t => t.id === selectedTemplateId);
  const isNative = Capacitor.isNativePlatform();

  const carouselSideW = 210;
  const carouselSideOffset = 170;
  const carouselCenterW = 320;
  const carouselStageMinH = 360;

  const runWebAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      setLocalError(t('optimizer.uploadStep.enterPrompt'));
      return;
    }
    setLocalError(null);
    setAiGenerateLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY || ''}`,
        },
        body: JSON.stringify({
          rawInput: aiPrompt.trim(),
          model: 'groq-llama-3.3-70b-versatile',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLocalError(data.error || `Failed (${res.status})`);
        return;
      }
      const text = (data.generatedResume || '').trim();
      if (!text) {
        setLocalError(t('optimizer.uploadStep.noResumeReturned'));
        return;
      }
      setAiGeneratedText(text);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setAiGenerateLoading(false);
    }
  };

  /* ─── NATIVE MOBILE DESIGN ──────────────────────────── */
  if (isNative) {
    const aiFeatures: Array<{ Icon: React.FC<{ style?: React.CSSProperties; className?: string }>, label: string, desc: string }> = [
      { Icon: Target as any,   label: 'ATS Score Analysis',   desc: 'Instant keyword match scoring' },
      { Icon: Sparkles as any, label: 'AI Optimization',      desc: 'Tailored for every job description' },
      { Icon: Palette as any,  label: '24 Pro Templates',     desc: 'Apply a design skin in one tap' },
      { Icon: FileText as any, label: 'Edit & Refine',        desc: 'Fill in, design, add sections' },
      { Icon: Layout as any,  label: 'Job Match & Export',    desc: 'Download PDF/DOCX, apply & track' },
    ];

    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--vscode-bg-primary)', isolation: 'isolate' as const }}>

        {/* ── Purple gradient splash header ───────────────────── */}
        <div style={{
          background: 'var(--ggj-header-bg)',
          paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
          paddingBottom: 28, paddingLeft: 20, paddingRight: 20,
          flexShrink: 0,
          position: 'relative', overflow: 'hidden',
          borderRadius: '0 0 24px 24px',
          boxShadow: 'var(--ggj-btn-shadow, 0 4px 16px rgba(0,0,0,0.12))',
        }}>
          {/* Decorative blobs */}
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.18)', borderRadius: 20, padding: '4px 12px', marginBottom: 10 }}>
            <Sparkles style={{ width: 11, height: 11, color: '#fff' }} />
            <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', letterSpacing: '0.10em', textTransform: 'uppercase' }}>AI-Powered</span>
          </div>

          {/* Title */}
          <h1 style={{ fontSize: 24, fontWeight: 900, color: '#fff', margin: '0 0 4px', lineHeight: 1.15, letterSpacing: '-0.3px' }}>Resume Optimizer</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', margin: 0, lineHeight: 1.4 }}>Upload your resume and let AI do the heavy lifting</p>

          {/* Feature pills */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {aiFeatures.map(({ Icon, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.14)', borderRadius: 20, padding: '5px 10px' }}>
                <Icon style={{ width: 12, height: 12, color: '#fff', flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Scrollable body (stacks below sticky bar) ── */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' as any, padding: '20px 16px 16px', position: 'relative' as const, zIndex: 0 }}>

          {roleFitFetchError && (
            <p style={{ fontSize: 13, color: '#ef4444', marginBottom: 14, padding: '10px 12px', borderRadius: 12, background: 'rgba(239,68,68,0.12)' }}>
              {roleFitFetchError}
            </p>
          )}

          {roleFitPrefetchLoading && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: 12 }}>
              <Loader2 style={{ width: 36, height: 36, color: 'var(--vscode-primary)' }} className="animate-spin" />
            </div>
          )}

          {roleFitContext && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--vscode-text-tertiary)', margin: 0 }}>Resume source</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(['preloaded', 'upload', 'ai'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => {
                      setRoleFitMainTab(tab);
                      setLocalError(null);
                      if (tab !== 'ai') setInputMode('upload');
                      else setInputMode('ai');
                    }}
                    style={{
                      flex: '1 1 30%',
                      minWidth: 96,
                      padding: '10px 8px',
                      borderRadius: 12,
                      border: 'none',
                      fontWeight: 800,
                      fontSize: 11,
                      cursor: 'pointer',
                      background: roleFitMainTab === tab ? 'var(--brand-cta-bg)' : 'var(--vscode-bg-tertiary)',
                      color: roleFitMainTab === tab ? 'var(--vscode-primary-foreground, #fff)' : 'var(--vscode-text-secondary)',
                    }}
                  >
                    {tab === 'preloaded'
                      ? t('optimizer.uploadStep.tabPreloaded', 'Preloaded')
                      : tab === 'upload'
                        ? t('optimizer.uploadStep.tabUploadShort', 'Upload')
                        : t('optimizer.uploadStep.tabGenerateAi', 'AI')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Upload zone / file card — RoleFit uses tab; default flow unchanged when !roleFitContext */}
          {roleFitContext && roleFitMainTab === 'preloaded' ? (
            <div style={{ marginBottom: 16, padding: 16, borderRadius: 16, border: '1px solid var(--brand-accent-border)', background: 'var(--vscode-bg-elevated)' }}>
              <p style={{ fontSize: 13, color: 'var(--vscode-text-primary)', marginBottom: 10, lineHeight: 1.45 }}>
                {t('optimizer.uploadStep.roleFitPreloadedLead', 'Talent profile text from your CRM. Use it as the resume for this match.')}
              </p>
              <div style={{ maxHeight: 200, overflowY: 'auto', padding: 12, borderRadius: 12, background: 'var(--vscode-bg-secondary)', fontSize: 12, lineHeight: 1.45, whiteSpace: 'pre-wrap', color: 'var(--vscode-text-primary)', marginBottom: 12 }}>
                {roleFitContext.profileResumeText?.trim()
                  ? roleFitContext.profileResumeText
                  : t('optimizer.uploadStep.noProfileText', 'No profile resume text on file.')}
              </div>
              <button
                type="button"
                onClick={applyRoleFitProfile}
                disabled={!roleFitContext.profileResumeText?.trim()}
                style={{
                  width: '100%',
                  minHeight: 44,
                  borderRadius: 12,
                  background: roleFitContext.profileResumeText?.trim() ? 'var(--brand-cta-bg)' : 'var(--vscode-bg-tertiary)',
                  border: 'none',
                  color: 'var(--vscode-primary-foreground, #fff)',
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: roleFitContext.profileResumeText?.trim() ? 'pointer' : 'not-allowed',
                  opacity: roleFitContext.profileResumeText?.trim() ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <Sparkles style={{ width: 16, height: 16 }} />
                {t('optimizer.uploadStep.useProfileResume', 'Use this resume')}
              </button>
            </div>
          ) : roleFitContext && roleFitMainTab === 'ai' ? (
            <div style={{ marginBottom: 20 }}>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={t('optimizer.uploadStep.aiPlaceholder')}
                rows={4}
                style={{
                  width: '100%',
                  borderRadius: 14,
                  border: '1.5px solid var(--brand-accent-border)',
                  padding: '12px 14px',
                  fontSize: 13,
                  color: 'var(--vscode-text-primary)',
                  background: 'var(--vscode-bg-elevated)',
                  resize: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={runWebAiGenerate}
                  disabled={!aiPrompt.trim() || aiGenerateLoading}
                  style={{
                    flex: 1,
                    minHeight: 44,
                    borderRadius: 12,
                    background: 'var(--brand-cta-bg)',
                    border: 'none',
                    color: 'var(--vscode-primary-foreground, #fff)',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: !aiPrompt.trim() || aiGenerateLoading ? 0.5 : 1,
                  }}
                >
                  {aiGenerateLoading ? (
                    <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" />
                  ) : (
                    <Sparkles style={{ width: 16, height: 16 }} />
                  )}
                  {aiGenerateLoading
                    ? t('optimizer.uploadStep.generating')
                    : t('optimizer.uploadStep.generateResume')}
                </button>
              </div>
              {aiGeneratedText && (
                <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: 'var(--vscode-bg-secondary)', maxHeight: 180, overflowY: 'auto', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {aiGeneratedText}
                </div>
              )}
              {aiGeneratedText && (
                <button
                  type="button"
                  onClick={() => {
                    setResumeText(aiGeneratedText);
                    setHasResume(true);
                    setResumeFile(new File([aiGeneratedText], 'resume-generated.txt', { type: 'text/plain' }));
                    setParsedSectionsFromDocument(null);
                    setParsedStructured(null);
                    setParsingMetadata({ confidence: 0.75, pageCount: 1, processingTime: 0 });
                    setAiGeneratedText(null);
                    setRoleFitMainTab('upload');
                  }}
                  style={{
                    width: '100%',
                    marginTop: 10,
                    minHeight: 44,
                    borderRadius: 12,
                    background: 'var(--brand-cta-bg)',
                    border: 'none',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {t('optimizer.uploadStep.useGeneratedResume', 'Use generated resume')}
                </button>
              )}
              {localError && (
                <p style={{ fontSize: 12, color: '#ef4444', marginTop: 10 }}>{localError}</p>
              )}
            </div>
          ) : !resumeFile ? (
            <label htmlFor="resume-upload-native" style={{ display: 'block', cursor: 'pointer', marginBottom: 16 }}>
              <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileUpload}
                className="hidden" id="resume-upload-native" disabled={isLoading || isExtracting} />
              <div style={{
                border: '2px dashed var(--brand-accent-border)',
                borderRadius: 20,
                background: 'var(--vscode-bg-elevated)',
                padding: '24px 16px 20px',
                textAlign: 'center',
                boxShadow: 'var(--vscode-shadow-sm)',
              }}>
                {/* Upload icon — larger icon, less empty space */}
                <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 14px' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--brand-accent-bg)', animation: 'pulse 2s ease-in-out infinite' }} />
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--brand-cta-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--brand-cta-shadow)', position: 'relative' }}>
                    <Upload style={{ width: 38, height: 38, color: 'var(--vscode-primary-foreground, #fff)' }} />
                  </div>
                </div>
                <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--vscode-text-primary)', marginBottom: 4 }}>Drop your resume here</p>
                <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)', marginBottom: 14, lineHeight: 1.5 }}>PDF, DOC, DOCX, TXT · powered by LangChain</p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--brand-cta-bg)', borderRadius: 14, padding: '12px 28px', color: 'var(--vscode-primary-foreground, #fff)', fontSize: 14, fontWeight: 800, boxShadow: 'var(--brand-cta-shadow)', WebkitTapHighlightColor: 'transparent' }}>
                  <Upload style={{ width: 18, height: 18 }} />{' '}
                  {t('optimizer.uploadStep.tapToBrowse', 'Tap to browse')}
                </div>
              </div>
            </label>
          ) : (
            /* File uploaded card */
            <div style={{ borderRadius: 20, background: 'var(--vscode-bg-elevated)', border: '1.5px solid var(--brand-accent-border)', padding: '16px', marginBottom: 16, boxShadow: 'var(--vscode-shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: isExtracting ? 'var(--brand-accent-bg)' : hasResume ? 'var(--vscode-success-bg)' : 'var(--brand-accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isExtracting
                    ? <Loader2 style={{ width: 24, height: 24, color: 'var(--vscode-primary)' }} className="animate-spin" />
                    : hasResume
                    ? <CheckCircle style={{ width: 24, height: 24, color: 'var(--vscode-success)' }} />
                    : <FileText style={{ width: 24, height: 24, color: 'var(--vscode-primary)' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--vscode-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{resumeFile.name}</p>
                  <p style={{ fontSize: 12, color: 'var(--vscode-text-tertiary)', margin: '3px 0 0' }}>
                    {isExtracting ? 'Analyzing your resume…'
                      : hasResume ? `${(resumeFile.size / 1024).toFixed(1)} KB · ${Math.round((parsingMetadata?.confidence || 0) * 100)}% match`
                      : `${(resumeFile.size / 1024).toFixed(1)} KB`}
                  </p>
                </div>
                {!isExtracting && (
                  <button type="button" onClick={clearResume}
                    style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--vscode-bg-tertiary)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                    <X style={{ width: 16, height: 16, color: 'var(--vscode-text-secondary)' }} />
                  </button>
                )}
              </div>
              {(extractionError || localError) && (
                <p style={{ fontSize: 12, color: '#ef4444', marginTop: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <AlertTriangle style={{ width: 14, height: 14, flexShrink: 0 }} />{extractionError || localError}
                </p>
              )}
              {/* Detected skills chips */}
              {hasResume && detectedSkills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {detectedSkills.slice(0, 10).map((skill, i) => (
                    <span key={i} style={{ padding: '4px 10px', borderRadius: 20, background: 'var(--brand-accent-bg)', color: 'var(--vscode-primary)', fontSize: 11, fontWeight: 600 }}>{skill}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Create with AI link */}
          {!roleFitContext && inputMode !== 'ai' && !resumeFile && (
            <button type="button" onClick={() => { setInputMode('ai'); setLocalError(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: 'var(--vscode-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 20, padding: 0, WebkitTapHighlightColor: 'transparent' }}>
              <Sparkles style={{ width: 14, height: 14 }} />
              {t('optimizer.uploadStep.createWithAI')}
            </button>
          )}

          {/* AI generate mode (non–RoleFit native only; RoleFit uses tabbed AI block above) */}
          {!roleFitContext && inputMode === 'ai' && (
            <div style={{ marginBottom: 20 }}>
              <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={t('optimizer.uploadStep.aiPlaceholder')} rows={4}
                style={{ width: '100%', borderRadius: 14, border: '1.5px solid var(--brand-accent-border)', padding: '12px 14px', fontSize: 13, color: 'var(--vscode-text-primary)', background: 'var(--vscode-bg-elevated)', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button type="button" onClick={async () => {
                  if (!aiPrompt.trim()) return;
                  setAiGenerateLoading(true);
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-resume`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY || ''}` },
                      body: JSON.stringify({ rawInput: aiPrompt.trim(), model: 'groq-llama-3.3-70b-versatile' })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) { setLocalError(data.error || `Failed`); return; }
                    const text = (data.generatedResume || '').trim();
                    if (text) {
                      setResumeText(text); setHasResume(true);
                      setResumeFile(new File([text], 'resume-generated.txt', { type: 'text/plain' }));
                      setAiGeneratedText(null); setInputMode('upload');
                    }
                  } catch (err) { setLocalError(err instanceof Error ? err.message : 'Failed'); }
                  finally { setAiGenerateLoading(false); }
                }} disabled={!aiPrompt.trim() || aiGenerateLoading}
                  style={{ flex: 1, minHeight: 44, borderRadius: 12, background: 'var(--brand-cta-bg)', border: 'none', color: 'var(--vscode-primary-foreground, #fff)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: !aiPrompt.trim() || aiGenerateLoading ? 0.5 : 1 }}>
                  {aiGenerateLoading ? <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> : <Sparkles style={{ width: 16, height: 16 }} />}
                  {aiGenerateLoading
                    ? t('optimizer.uploadStep.generating')
                    : t('optimizer.uploadStep.generateResume')}
                </button>
                <button type="button" onClick={() => { setInputMode('upload'); setLocalError(null); }}
                  style={{ minHeight: 44, padding: '0 16px', borderRadius: 12, border: '1.5px solid var(--brand-accent-border)', background: 'transparent', color: 'var(--vscode-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* What happens next — compact tiles, larger icons, all app steps */}
          {!roleFitContext && !resumeFile && inputMode !== 'ai' && (
            <div style={{ marginTop: 6 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>What happens next</p>
              {aiFeatures.map(({ Icon, label, desc }, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--vscode-bg-elevated)', marginBottom: 6, border: '1px solid var(--vscode-border-primary)', boxShadow: 'var(--vscode-shadow-sm)' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon style={{ width: 20, height: 20, color: 'var(--vscode-primary)' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--vscode-text-primary)', margin: 0 }}>{label}</p>
                    <p style={{ fontSize: 10, color: 'var(--vscode-text-tertiary)', margin: '1px 0 0' }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Sticky bottom bar: always on top of scroll content ── */}
        <div style={{
          flexShrink: 0,
          position: 'relative' as const,
          zIndex: 200,
          padding: '10px 16px 12px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          background: 'var(--ggj-nav-bg, var(--vscode-bg-elevated))',
          borderTop: '1px solid var(--vscode-border-primary)',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'stretch',
          gap: 10,
        }}>
          <button type="button" onClick={handleContinueToPreview}
            disabled={continueDisabled}
            style={{
              flex: 1, minHeight: 52, borderRadius: 16,
              background: !continueDisabled
                ? 'var(--brand-cta-bg)'
                : 'var(--vscode-bg-tertiary)',
              border: 'none',
              color: !continueDisabled ? 'var(--vscode-primary-foreground, #fff)' : 'var(--vscode-text-tertiary)',
              fontSize: 15, fontWeight: 800,
              cursor: !continueDisabled ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: !continueDisabled ? 'var(--brand-cta-shadow)' : 'none',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.2px',
            }}>
            {isContinueLoading ? <Loader2 style={{ width: 20, height: 20 }} className="animate-spin" /> : <ArrowRight style={{ width: 20, height: 20 }} />}
            {isContinueLoading ? 'Processing…' : continueCtaLabel}
          </button>
          <OptimizerMoreMenu buttonStyle={{ minWidth: 52 }} />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={STEP_MOTION_CLASS}
    >
      <div className={STEP_WRAPPER_CLASS}>
        {/* py-4 instead of pt-8/pt-10/pt-12 — the title bar above already provides spacing */}
        <div className={`${STEP_SCROLL_AREA_CLASS} py-4 relative`}>
          {roleFitFetchError && (
            <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400">
              {roleFitFetchError}
            </div>
          )}
          {roleFitPrefetchLoading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-black/35">
              <Loader2 className="h-10 w-10 animate-spin" style={{ color: 'var(--vscode-primary)' }} />
            </div>
          )}
          <div className="flex w-full min-w-0 flex-col gap-4 lg:gap-5">
            {/* RoleFit: single resume card (candidate & role live in shell breadcrumb + header) */}
            <div
              className={
                roleFitContext
                  ? ROLEFIT_CARD_CLASS
                  : 'flex min-h-0 flex-col overflow-hidden rounded-2xl shadow-sm'
              }
              style={BRAND_CARD_BORDER}
            >

              {/* Header: RoleFit uses vscode panel header (matches ATS templates column) */}
              {roleFitContext ? (
                <div className={`${STEP_INNER_HEADER_CLASS} flex shrink-0 items-start gap-3 sm:px-5 sm:py-4`}>
                  <div className={STEP_HEADER_ICON_CLASS} aria-hidden>
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <h2 className="vscode-text-primary mb-0 text-base font-bold">
                      {t('optimizer.uploadStep.roleFitResumeCardTitle', 'Resume for matching')}
                    </h2>
                    <p className="vscode-text-secondary mt-1 text-xs leading-snug">
                      {t(
                        'optimizer.uploadStep.roleFitResumeCardSubtitle',
                        'Preloaded profile, file upload, or AI — choose how to supply resume text for this job.'
                      )}
                    </p>
                  </div>
                  {roleFitMainTab === 'ai' && (
                    <button
                      type="button"
                      onClick={() => {
                        setRoleFitMainTab('upload');
                        setInputMode('upload');
                        setLocalError(null);
                        clearResume();
                        setAiGeneratedText(null);
                      }}
                      className="shrink-0 text-xs font-semibold text-[#6C47FF] hover:underline dark:text-[#5B8DFF]"
                    >
                      {t('optimizer.uploadStep.backToSources', 'Change source')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="p-4 sm:p-5" style={{ background: 'var(--brand-cta-bg)' }}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-black/10">
                      <Upload className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="mb-0 text-base font-bold text-white">{t('optimizer.uploadStep.title')}</h2>
                      <p className="mt-0.5 text-xs text-white/70">{t('optimizer.uploadStep.subtitle')}</p>
                    </div>
                    {inputMode === 'ai' && (
                      <button
                        type="button"
                        onClick={() => { setInputMode('upload'); setLocalError(null); clearResume(); }}
                        className="shrink-0 text-xs text-white/80 underline hover:text-white"
                      >
                        {t('optimizer.uploadStep.uploadInstead', 'Upload instead')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div
                className={`scrollbar-hide flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5${
                  roleFitContext ? ' vscode-text-primary' : ''
                }`}
              >
              {roleFitContext && (
                <div className="flex flex-col gap-1.5 text-left">
                  <p className="vscode-text-tertiary text-[10px] font-semibold uppercase tracking-wide">
                    {t('optimizer.uploadStep.resumeSource', 'Resume source')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(['preloaded', 'upload', 'ai'] as const).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => {
                          setRoleFitMainTab(tab);
                          setLocalError(null);
                          setInputMode(tab === 'ai' ? 'ai' : 'upload');
                        }}
                        className={`min-w-[6.5rem] flex-1 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-colors sm:text-sm ${
                          roleFitMainTab === tab
                            ? 'border-[var(--vscode-button-border,transparent)] bg-[var(--vscode-button-background)] text-[var(--vscode-button-foreground)] shadow-sm'
                            : 'border-[var(--vscode-border-primary)] bg-[var(--vscode-button-secondaryBackground)] text-[var(--vscode-button-secondaryForeground)] hover:bg-[var(--vscode-button-secondaryHoverBackground,var(--vscode-bg-secondary))]'
                        }`}
                      >
                        {tab === 'preloaded'
                          ? t('optimizer.uploadStep.tabPreloaded', 'Preloaded data')
                          : tab === 'upload'
                            ? t('optimizer.uploadStep.tabUpload', 'Upload resume')
                            : t('optimizer.uploadStep.tabGenerateAi', 'Generate with AI')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <AnimatePresence mode="wait">

                {roleFitContext && roleFitMainTab === 'preloaded' && (
                  <motion.div
                    key="rolefit-profile"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    className="flex flex-col gap-4"
                  >
                    <div className="rounded-xl border border-solid border-gray-200 bg-gray-50 p-5 dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)]">
                      <p className="vscode-text-secondary mb-3 text-left text-sm">
                        {t(
                          'optimizer.uploadStep.roleFitProfileHint',
                          'Prefilled from the talent record. Use this as resume text for matching and optimization.'
                        )}{' '}
                        <span className="vscode-text-primary font-semibold">{roleFitContext.candidateLabel}</span>
                      </p>
                      <div className="vscode-text-primary max-h-[280px] overflow-y-auto rounded-lg border border-solid border-gray-200 bg-white p-4 text-left text-sm leading-relaxed whitespace-pre-wrap dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)]">
                        {roleFitContext.profileResumeText?.trim() ? (
                          roleFitContext.profileResumeText
                        ) : (
                          <span className="vscode-text-secondary italic">
                            {t('optimizer.uploadStep.noProfileText', 'No profile resume text on file.')}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={applyRoleFitProfile}
                        disabled={!roleFitContext.profileResumeText?.trim()}
                        className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-2.5 font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${
                          roleFitContext.profileResumeText?.trim()
                            ? STEP_PRIMARY_CTA_CLASS
                            : 'border border-gray-300 bg-gray-200 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        <Sparkles className="w-4 h-4" />
                        {t('optimizer.uploadStep.useProfileResume', 'Use this resume')}
                      </button>
                    </div>
                  </motion.div>
                )}

                  {/* Upload mode */}
                {inputMode === 'upload' &&
                  (!roleFitContext || roleFitMainTab === 'upload') && (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    className="flex flex-col gap-4"
                  >
                    {!resumeFile ? (
                      <div
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        className="flex min-h-[160px] cursor-pointer flex-col justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/80 p-6 text-center transition-colors hover:border-[#6C47FF] dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)] dark:hover:border-[#5B8DFF]"
                      >
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="resume-upload"
                          disabled={isLoading || isExtracting}
                        />
                        <label htmlFor="resume-upload" className="cursor-pointer">
                          <Upload className="vscode-text-tertiary mx-auto mb-3 h-14 w-14" />
                          <p className="vscode-text-primary mb-1 font-medium">
                            {t('optimizer.uploadStep.dropHere')}
                          </p>
                          <p className="vscode-text-secondary text-sm">
                            {t('optimizer.uploadStep.fileFormats')}
                          </p>
                        </label>
                      </div>
                    ) : (
                      <div className="space-y-3 rounded-xl border border-solid border-gray-200 bg-gray-50 p-4 dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)]">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                            style={{
                              backgroundColor: 'var(--brand-accent-bg)',
                              border: '1px solid var(--brand-accent-border)',
                            }}
                          >
                            <FileText className="h-5 w-5" style={{ color: 'var(--vscode-primary)' }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="vscode-text-primary truncate font-medium">
                              {resumeFile.name}
                            </p>
                            <p className="vscode-text-secondary text-sm">
                              {(resumeFile.size / 1024).toFixed(1)} KB
                                {parsingMetadata && ` · ${Math.round((parsingMetadata.confidence || 0) * 100)}% confidence`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {isExtracting ? (
                              <Loader2 className="w-5 h-5 text-[#6C47FF] animate-spin" />
                            ) : hasResume ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : extractionError ? (
                              <AlertTriangle className="w-5 h-5 text-amber-500" />
                            ) : null}
                            <button
                              type="button"
                              onClick={clearResume}
                              className="vscode-text-tertiary hover:text-red-500 p-1 transition-colors"
                              title={t('optimizer.uploadStep.removeFile')}
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        {(extractionError || error || localError) && (
                          <p className="text-sm text-red-500 dark:text-red-400 flex items-start gap-1">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            {extractionError || error || localError}
                          </p>
                        )}
                        {hasResume && detectedSkills.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {detectedSkills.slice(0, 8).map((skill, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 rounded-md text-xs font-medium bg-[#6C47FF]/15 text-[#6C47FF] dark:bg-[#5B8DFF]/20 dark:text-[#5B8DFF]"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}

                  {/* AI Generate mode (standard flow or RoleFit “Generate with AI” tab) */}
                {((!roleFitContext && inputMode === 'ai') || (roleFitContext && roleFitMainTab === 'ai')) && (
                  <motion.div
                    key="ai"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    className="flex flex-col gap-4"
                  >
                    <div className="rounded-xl border border-solid border-gray-200 bg-gray-50 p-5 text-left dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)]">
                      {aiGeneratedText ? (
                        <>
                          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-solid border-gray-200 bg-white p-4 dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)]">
                            <div className="vscode-text-primary whitespace-pre-wrap text-sm leading-relaxed">
                              {aiGeneratedText}
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setResumeText(aiGeneratedText);
                                setHasResume(true);
                                setResumeFile(new File([aiGeneratedText], 'resume-generated.txt', { type: 'text/plain' }));
                                setParsedSectionsFromDocument(null);
                                setParsedStructured(null);
                                setParsingMetadata({ confidence: 0.75, pageCount: 1, processingTime: 0 });
                                setAiGeneratedText(null);
                                if (roleFitContext) setRoleFitMainTab('upload');
                              }}
                              className={`flex items-center gap-2 ${STEP_PRIMARY_CTA_CLASS}`}
                            >
                              <ArrowRight className="w-4 h-4" />
                              {t('optimizer.uploadStep.continueWithResume')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setAiGeneratedText(null); setLocalError(null); }}
                              className="vscode-text-secondary hover:text-[#6C47FF] text-sm dark:hover:text-[#5B8DFF]"
                            >
                              {t('optimizer.uploadStep.backToPrompt')}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <textarea
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder={t('optimizer.uploadStep.aiPlaceholder')}
                            rows={4}
                            className="vscode-text-primary w-full resize-y rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm placeholder:text-[var(--vscode-text-tertiary)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#6C47FF] dark:border-[var(--vscode-border-primary)] dark:bg-[var(--vscode-bg-secondary)]"
                          />
                            <div className="mt-3 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void runWebAiGenerate()}
                              disabled={!aiPrompt.trim() || aiGenerateLoading}
                              className={`flex items-center gap-2 ${STEP_PRIMARY_CTA_CLASS} disabled:bg-gray-400 disabled:opacity-50 dark:disabled:bg-gray-600`}
                            >
                              {aiGenerateLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4" />
                              )}
                              {aiGenerateLoading
                                ? t('optimizer.uploadStep.generating')
                                : t('optimizer.uploadStep.generateResume')}
                            </button>
                          </div>
                          {localError && (
                              <p className="text-sm text-red-500 dark:text-red-400 flex items-center gap-1 mt-2">
                              <AlertTriangle className="w-4 h-4 shrink-0" />
                              {localError}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

                {/* Create with AI link */}
              {!roleFitContext && inputMode !== 'ai' && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-solid pt-3 vscode-border">
                  <button
                    type="button"
                    onClick={() => { setInputMode('ai'); setLocalError(null); }}
                    className="vscode-text-secondary hover:text-[#6C47FF] inline-flex items-center gap-1.5 text-sm font-medium underline dark:hover:text-[#5B8DFF]"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {t('optimizer.uploadStep.createWithAI')}
                  </button>
                </div>
              )}

              {/* Tips */}
                {!roleFitContext && (
                <div className="border-t border-solid pt-3 vscode-border">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#6C47FF] dark:text-[#5B8DFF] mb-2">{t('optimizer.uploadStep.tips')}</h4>
                <ul className="vscode-text-secondary space-y-1 text-xs sm:text-sm">
                  <li>{t('optimizer.uploadStep.tip1')}</li>
                  <li>{t('optimizer.uploadStep.tip2')}</li>
                  <li>{t('optimizer.uploadStep.tip3')}</li>
                </ul>
              </div>
                )}
              </div>
            </div>

            {/* ── ATS templates: only for default upload flow (not RoleFit) ───────────────── */}
            {!roleFitContext && (
            <div className="flex flex-col h-full rounded-2xl overflow-hidden shadow-sm min-w-0" style={{ border: '1px solid var(--brand-input-border)' }}>

              {/* Header */}
              <div className="flex-shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b vscode-border vscode-bg-secondary">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--brand-cta-bg)' }}>
                  <Layout className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-base font-bold vscode-text block">{t('optimizer.atsTemplates', 'ATS Templates')}</span>
                  <p className="text-xs vscode-text-secondary mt-0.5">{carouselIdx + 1}/{totalTemplates} · {t('optimizer.atsTemplatesHint', 'ATS-compliant')}</p>
                </div>
                <button type="button" onClick={() => setShowTemplateLibrary(true)}
                  className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#6C47FF] dark:text-[#5B8DFF] hover:underline">
                  <BookOpen className="w-3.5 h-3.5" />
                  {t('optimizer.browseAll', 'All →')}
                </button>
              </div>

              {/* ── MOBILE: 5-col grid ── */}
              <div className="lg:hidden p-3">
                <div className="grid grid-cols-5 gap-1.5">
                  {TEMPLATE_LIBRARY.map((tpl, i) => {
                    const isSel = selectedTemplateId === tpl.id;
                    return (
                      <button key={tpl.id} type="button"
                        onClick={() => { setSelectedTemplateId(tpl.id); setCarouselIdx(i); }}
                        className={`rounded-lg overflow-hidden border-2 focus:outline-none transition-all ${isSel ? 'border-[#6C47FF] ring-1 ring-[#6C47FF]/30' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'}`}>
                        <div style={{ background: tpl.hBg, height: 28 }} />
                        <div style={{ background: tpl.bodyBg || '#f9fafb', padding: '4px 5px' }}>
                          {[75, 100, 60].map((w, j) => (
                            <div key={j} style={{ background: '#d1d5db', height: 3, width: `${w}%`, borderRadius: 2, marginBottom: 2 }} />
                          ))}
                        </div>
                        <div className={`px-1.5 py-1 flex items-center justify-between border-t ${isSel ? 'bg-[#6C47FF]/8 border-[#6C47FF]/20' : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}>
                          <span className={`text-[9px] font-semibold leading-tight truncate ${isSel ? 'text-[#6C47FF]' : 'text-gray-700 dark:text-gray-300'}`}>{tpl.label}</span>
                          {isSel && <CheckCircle className="w-2.5 h-2.5 text-[#6C47FF] shrink-0 ml-1" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── DESKTOP: playing-card stacked carousel ── */}
              <div className="hidden lg:flex flex-1 min-h-0 flex-col overflow-hidden">
                {/* Card stage — grows to fill remaining panel height */}
                <div className="relative w-full flex-1 overflow-hidden" style={{ minHeight: carouselStageMinH }}>

                  {/* Left card — fanned behind, rotated left — click = prev */}
                  {(() => {
                    const prevI = wrapIdx(carouselIdx - 1);
                    const cfg = TEMPLATE_LIBRARY[prevI];
                    const SW = carouselSideW;
                    const SS = SW / 220;
                    return (
                      <button type="button" onClick={handleCarouselPrev} title={cfg.label}
                        className="absolute focus:outline-none rounded-2xl overflow-hidden shadow-md border border-gray-200/70"
                        style={{
                          width: SW, height: Math.round(310 * SS),
                          left: '50%', top: '50%',
                          transform: `translate(calc(-50% - ${carouselSideOffset}px), -50%) rotate(-6deg)`,
                          zIndex: 3, opacity: 0.72, background: '#fff',
                          transition: 'all 0.25s ease',
                        }}>
                        <div style={{ width: 220, height: 310, transform: `scale(${SS})`, transformOrigin: 'top left', overflow: 'hidden', pointerEvents: 'none' }}>
                          <ResumeDocumentContent cfg={cfg} />
                        </div>
                        <div className="absolute bottom-0 inset-x-0 px-2 py-1.5" style={{ background: 'rgba(0,0,0,0.5)' }}>
                          <span className="text-[10px] font-semibold text-white truncate block">{cfg.label}</span>
                        </div>
                      </button>
                    );
                  })()}

                  {/* Right card — fanned behind, rotated right — click = next */}
                  {(() => {
                    const nextI = wrapIdx(carouselIdx + 1);
                    const cfg = TEMPLATE_LIBRARY[nextI];
                    const SW = carouselSideW;
                    const SS = SW / 220;
                    return (
                      <button type="button" onClick={handleCarouselNext} title={cfg.label}
                        className="absolute focus:outline-none rounded-2xl overflow-hidden shadow-md border border-gray-200/70"
                        style={{
                          width: SW, height: Math.round(310 * SS),
                          left: '50%', top: '50%',
                          transform: `translate(calc(-50% + ${carouselSideOffset}px), -50%) rotate(6deg)`,
                          zIndex: 3, opacity: 0.72, background: '#fff',
                          transition: 'all 0.25s ease',
                        }}>
                        <div style={{ width: 220, height: 310, transform: `scale(${SS})`, transformOrigin: 'top left', overflow: 'hidden', pointerEvents: 'none' }}>
                          <ResumeDocumentContent cfg={cfg} />
                        </div>
                        <div className="absolute bottom-0 inset-x-0 px-2 py-1.5" style={{ background: 'rgba(0,0,0,0.5)' }}>
                          <span className="text-[10px] font-semibold text-white truncate block">{cfg.label}</span>
                        </div>
                      </button>
                    );
                  })()}

                  {/* Center card — wide, fills the stage */}
                  {(() => {
                    const cfg = TEMPLATE_LIBRARY[carouselIdx];
                    const isSel = selectedTemplateId === cfg.id;
                    const CW = carouselCenterW;
                    const CS = CW / 220;
                    const CH = Math.round(310 * CS);
                    return (
                      <button type="button" onClick={() => handleSelectCarouselTemplate(carouselIdx)}
                        title={`${cfg.label} — click to select`}
                        className={`absolute focus:outline-none rounded-2xl overflow-hidden shadow-2xl border-2 transition-all ${isSel ? 'border-[#6C47FF] ring-4 ring-[#6C47FF]/20' : 'border-white hover:border-[#6C47FF]/50'}`}
                        style={{
                          width: CW, height: CH,
                          left: '50%', top: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 10, background: '#fff',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}>
                        <div style={{ width: 220, height: 310, transform: `scale(${CS})`, transformOrigin: 'top left', overflow: 'hidden', pointerEvents: 'none' }}>
                          <ResumeDocumentContent cfg={cfg} />
                        </div>
                        {isSel && (
                          <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#6C47FF] flex items-center justify-center shadow-lg z-20">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })()}

                  {/* Nav arrows */}
                  <button type="button" onClick={handleCarouselPrev}
                    className="absolute left-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-solid bg-white shadow-lg ring-2 ring-gray-900/5 transition-all hover:scale-105 dark:bg-[var(--brand-accent-bg)] dark:ring-white/15"
                    style={{
                      top: '50%',
                      transform: 'translateY(-50%)',
                      borderColor: 'var(--brand-accent-border)',
                    }}
                    aria-label="Previous template">
                    <ChevronLeft className="h-4 w-4" style={{ color: 'var(--vscode-primary)' }} />
                  </button>
                  <button type="button" onClick={handleCarouselNext}
                    className="absolute right-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-solid bg-white shadow-lg ring-2 ring-gray-900/5 transition-all hover:scale-105 dark:bg-[var(--brand-accent-bg)] dark:ring-white/15"
                    style={{
                      top: '50%',
                      transform: 'translateY(-50%)',
                      borderColor: 'var(--brand-accent-border)',
                    }}
                    aria-label="Next template">
                    <ChevronRight className="h-4 w-4" style={{ color: 'var(--vscode-primary)' }} />
                  </button>
                </div>

                {/* Template info below stage */}
                <div className="px-5 pb-2 pt-3 text-center">
                  <p className="text-base font-bold vscode-text">
                    {TEMPLATE_LIBRARY[carouselIdx].label}
                    {TEMPLATE_LIBRARY[carouselIdx].ats && (
                      <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-bold tracking-wider align-middle">ATS</span>
                    )}
                  </p>
                  <p className="text-xs vscode-text-secondary mt-0.5">{TEMPLATE_LIBRARY[carouselIdx].category}</p>
                  <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                    {TEMPLATE_LIBRARY[carouselIdx].roles.slice(0, 3).map((role, i) => (
                      <span key={i} className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{role}</span>
                    ))}
                  </div>
                </div>

                {/* Progress bar */}
                <div className="px-5 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full rounded-full bg-[#6C47FF] transition-all duration-250"
                        style={{ width: `${((carouselIdx + 1) / totalTemplates) * 100}%` }} />
                    </div>
                    <span className="text-[11px] font-semibold text-[#6C47FF] tabular-nums shrink-0">{carouselIdx + 1}/{totalTemplates}</span>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 border-t border-solid p-4 sm:p-5 vscode-border">
                <button
                  type="button"
                  onClick={handleContinueToPreview}
                  disabled={continueDisabled}
                  className={`flex w-full items-center justify-center gap-2 ${STEP_PRIMARY_CTA_CLASS} px-4 py-3`}
                >
                  {isContinueLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {isContinueLoading ? t('optimizer.processing', 'Processing…') : continueCtaLabel}
                </button>
              </div>
            </div>
            )}

            {roleFitContext && (
              <div className={ROLEFIT_FOOTER_STRIP_CLASS}>
                <p className="vscode-text-secondary mb-3 text-center text-xs">
                  {t(
                    'optimizer.uploadStep.roleFitContinueHintNoTemplate',
                    'When your resume is ready, continue to match analysis.'
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleContinueToPreview}
                  disabled={continueDisabled}
                  className={WEB_CTA_BUTTON_CLASS}
                >
                  {isContinueLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <ArrowRight className="h-5 w-5" />
                  )}
                  {isContinueLoading ? t('optimizer.processing', 'Processing…') : continueCtaLabel}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {showTemplateLibrary && (
        <TemplateLibraryModal
          selectedId={selectedTemplateId}
          onSelect={(id) => { setSelectedTemplateId(id); setShowTemplateLibrary(false); }}
          onClose={() => setShowTemplateLibrary(false)}
        />
      )}
    </motion.div>
  );
};

export default React.memo(UploadStep);
